import { withNamespaces } from "react-i18next"
import { Alert, Button, Card, CardBody, CardHeader, Col, Form, Input, Row, Spinner } from "reactstrap";
import SimpleBar from "simplebar-react";
import defaultUserAvatar from "src/assets/images/users/def.svg";
import AIAvatar from "src/assets/images/users/ai.jpeg";
import { RiSendPlaneFill } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import AIAssistantChatService from "src/apis/AIAssistantChatService";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import ChatbotIcon from "src/assets/images/microchip.png";

import "./style.scss";

const defaultMaxHeight = 0;

const MAP_MODULES = {
    "wb": "wb",
    "cm": "cm",
    "gd": "gdpr",
    "tp": "tpdd"
}

const QuestionsBody = ({ t, user, historyQuery, newQuestions }) => {
    if (historyQuery.isFetching) {
        return (
            <div>
                <div className="mb-2">
                    <div className="mb-3">
                        <div
                            className="dt-field dt-skeleton mb-1"
                            style={{ width: '120px', height: '20px' }}
                        />
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '50%', height: '20px' }}
                        />
                    </div>
                    <div>
                        <div
                            className="dt-field dt-skeleton mb-1"
                            style={{ width: '80px', height: '20px' }}
                        />
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '40%', height: '20px' }}
                        />
                    </div>
                </div>

                <div className="d-flex flex-column" style={{ direction: 'rtl' }}>
                    <div className="mb-3">
                        <div
                            className="dt-field dt-skeleton mb-1"
                            style={{ width: '120px', height: '20px' }}
                        />
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '50%', height: '20px' }}
                        />
                    </div>
                    <div>
                        <div
                            className="dt-field dt-skeleton mb-1"
                            style={{ width: '80px', height: '20px' }}
                        />
                        <div
                            className="dt-field dt-skeleton"
                            style={{ width: '40%', height: '20px' }}
                        />
                    </div>
                </div>
            </div>
        )
    }

    const {
        history = []
    } = historyQuery.data || {};

    if (!(history.length + newQuestions.length)) {
        return (
            <Alert color="primary" className="mb-0">
                <h6 className="mb-1">
                    {t('How can I help?')}
                </h6>
                <small>
                    {t('Ask questions about platform features, workflows, and user guides.')}
                </small>
            </Alert>
        )
    }

    return (
        <ul className="list-unstyled mb-0 pr-3">
            {[...history, ...newQuestions].map((prevQuestion, index) => {
                const isAI = prevQuestion.role !== 'user';

                return (
                    <li className={`${!isAI ? 'right' : ''}`} key={index}>
                        <div className="conversation-list">
                            {isAI && (
                                <div className="chat-avatar">
                                    <img
                                        src={AIAvatar}
                                        alt={t('AI Assistant')}
                                    />
                                </div>
                            )}

                            <div className="ctext-wrap" style={{ maxWidth: '100%' }}>
                                <div className="conversation-name">
                                    {isAI ? 'Maya' : `${user.first_name} ${user.last_name}`}
                                    
                                </div>
                                <div
                                    className="ctext-wrap-content"
                                    dangerouslySetInnerHTML={{
                                        __html: prevQuestion.content
                                    }}
                                />
                                {/* <p className="chat-time mb-0">
                                    <i className="mdi mdi-clock-outline align-middle me-1"></i> 12:09
                                </p> */}
                            </div>

                            {!isAI && (
                                <div
                                    className="chat-avatar"
                                    style={{
                                        marginLeft: '8px',
                                        marginRight: '0px'
                                    }}
                                >
                                    <img
                                        src={user.avatar || defaultUserAvatar}
                                        alt={t('user')}
                                    />
                                </div>
                            )}
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}

const AiAssistantChat = ({ 
    t,
    isOpen, 
    orgId, 
    user,
    currentModule,
    boxRef,
    toggleOpen
}) => {
    const userId = user?.id;

    const [ maxHeight, setMaxHeight ] = useState(defaultMaxHeight);

    const [ newQuestions, setNewQuestions ] = useState([]);

    const formRef = useRef();
    const chatRef = useRef();
    const containerRef = useRef();
    const hasInitialScrollRef = useRef(false);

    // const queryClient = useQueryClient();

    const scrollChatToBottom = () => {
        const el = chatRef.current?.getScrollElement();

        if (!el) return;

        el.scrollTo({
            top: el.scrollHeight,
            behavior: 'smooth',
        });
    };

    const chatQueryKey = useMemo(() => {
        if (!orgId || !userId || !currentModule) return undefined;

        return ['ai-assistant-chat', currentModule, orgId, userId];
    }, [orgId, userId, currentModule]);

    const historyQuery = useQuery({
        queryKey: chatQueryKey,
        queryFn: async () => {
            const service = AIAssistantChatService.getInstance();

            return service.fetchUserChatHistory({
                userId,
                params: {
                    module: MAP_MODULES[currentModule]
                }
            });
        },
        enabled: !!chatQueryKey,
        refetchOnWindowFocus: false, 
        onError: () => {
            toast(t('Unable to load conversation history. Please try again.'), {
                type: 'error'
            })
        }
    });

    const askQuestionMutation = useMutation({
        mutationFn: async ({ question }) => {
            const service = AIAssistantChatService.getInstance();
            return await service.askQuestion({
                company_id: 'default_company',
                user_id: userId?.toString(),
                module: MAP_MODULES[currentModule],
                query: question
            })
        },
        onError: () => {
            toast(t('Something went wrong while processing your question. Please try again.'), {
                type: 'error'
            })
        },
        onSuccess: ({ answer }, { question }) => {
            setNewQuestions((prev) => {
                return [...prev, {
                    role: 'user',
                    content: question
                }, {
                    role: 'assistant',
                    content: answer
                }]
            });

            // queryClient.setQueryData(
            //     chatQueryKey,
            //     (oldData) => {
            //         return {
            //             ...oldData,
            //             history: [
            //                 ...(oldData?.history || []),
            //                 {
            //                     role: 'user',
            //                     content: question
            //                 },
            //                 {
            //                     role: 'assistant',
            //                     content: answer
            //                 }
            //             ]
            //         }
            //     }
            // );
        }
    });

    const handleSendQuesion = useCallback((e) => {
        e.preventDefault();

        if (askQuestionMutation.isLoading) return;

        const form = new FormData(e.currentTarget);
        const question = form.get('question');

        if (!question) return ;

        askQuestionMutation.mutate({
            question: form.get('question')
        }, {
            onSuccess: () => {
                formRef.current.reset();

                setTimeout(() => {
                    scrollChatToBottom();
                }, 300);
            }
        });
    }, [ askQuestionMutation ]);

    useLayoutEffect(() => {
        if (!isOpen ||!containerRef.current) return;

        const observer = new ResizeObserver(() => {
            const height = containerRef.current.clientHeight;

            if (height > 0) {
                setMaxHeight(height - 45);
            }
        });

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, [
        isOpen
    ]);

    useEffect(() => {
        if (
            !isOpen ||
            hasInitialScrollRef.current ||
            !historyQuery.data?.history?.length
        ) {
            return;
        }

        requestAnimationFrame(() => {
            scrollChatToBottom();
            hasInitialScrollRef.current = true;
        });
    }, [isOpen, historyQuery.data?.history]);

    if (!orgId || !userId || !currentModule) return null;

    return (
        <div 
            className={`ai-assistant-box ${isOpen ? 'open' : 'closed'}`}
            ref={boxRef}
        >
            <Card className="h-100">
                <CardHeader className="border-bottom bg-transparent">
                    <div className="user-chat-border">
                        <Row>
                            <Col md="12" className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <img
                                        src={AIAvatar}
                                        alt="Maya"
                                        className="rounded-circle me-2"
                                        width={38}
                                        height={38}
                                    />

                                    <div>
                                        <div className="d-flex align-items-center justify-content-start gap-1">
                                            <h5 className="font-size-15 mb-0">
                                                Maya
                                            </h5>
                                            <img
                                                src={ChatbotIcon}
                                                alt="Maya"
                                                className="rounded-circle me-2"
                                                width={16}
                                                height={16}
                                            />
                                        </div>

                                        <p className="text-muted mb-0">
                                            <i className="mdi mdi-circle text-success align-middle me-1"></i>
                                            {t('Ready to help')}
                                        </p>
                                    </div>
                                </div>

                                <Button
                                    color="link"
                                    size="sm"
                                    onClick={toggleOpen}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            </Col>
                        </Row>
                    </div>
                </CardHeader>

                <CardBody innerRef={containerRef}>
                    <div className="chat-widget">
                        <div className="chat-conversation" >
                            <SimpleBar ref={chatRef} style={{ maxHeight: `${maxHeight}px` }}>
                                <QuestionsBody 
                                    t={t}
                                    historyQuery={historyQuery}
                                    user={user}
                                    newQuestions={newQuestions}
                                />
                            </SimpleBar>
                        </div>
                    </div>
                </CardBody>
                <div className="p-3 chat-input-section border-top">
                    <Form
                        onSubmit={handleSendQuesion}
                        innerRef={formRef}
                        autoComplete="off"
                    >
                        <Row>
                            <Col>
                                <div>
                                    <Input 
                                        type="text" 
                                        className="form-control rounded chat-input pl-3" 
                                        placeholder={`${t('Ask a question about the platform')}...`} 
                                        name="question"
                                        autoComplete="off"
                                    />
                                </div>
                            </Col>
                            <Col xs={{ size: 'auto' }}>
                                <Button 
                                    color="primary" 
                                    type="submit" 
                                    className="chat-send w-md waves-effect waves-light"
                                    disabled={askQuestionMutation.isLoading}
                                >
                                    {askQuestionMutation.isLoading ? (
                                        <>
                                            <Spinner size="sm" className="me-1" />
                                            <span className="d-none d-sm-inline-block me-2">
                                                {t('Thinking')}...
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="d-none d-sm-inline-block me-2">
                                                {t('Send')}
                                            </span>
                                            <RiSendPlaneFill size={18} />
                                        </>
                                    )}
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </div>
            </Card>
        </div>
    )
} 

export default withNamespaces()(
    AiAssistantChat
);